import {inject, Injectable, signal} from "@angular/core";
import {Device, DeviceState} from "@models";
import {SimClientStorageService, SimDeviceService, SimInjectableDataService, SystemHub} from "@services";
import {EventEmitterService} from "@event-emitter";
import {Router} from "@angular/router";
import {timer} from "rxjs";
import {TranslateService} from "@ngx-translate/core";

/**
 * Handles registering a device with the server and keeping the device data up to date.
 */
@Injectable({
	providedIn: "root",
})
export class DeviceService {
	public device = signal<Device | null>(null);
	public deviceState = signal<DeviceState | null>(null);

	private readonly _deviceApiService = inject(SimDeviceService);
	private readonly _injectableDeviceDataService = inject(SimInjectableDataService);
	private readonly _simClientStorageService = inject(SimClientStorageService);
	private readonly _eventEmitter = inject(EventEmitterService);
	private readonly _router = inject(Router);
	private readonly _systemHub = inject(SystemHub);
	private readonly _translate = inject(TranslateService);

	private _isRegistered = false;

	async registerDevice() {
		if (this._isRegistered) {
			console.error("DeviceService.registerDevice() called multiple times.");
			return;
		}
		this._isRegistered = true;

		this._eventEmitter.onDeviceChanged.subscribe(({device}) => {
			if (this.device()?.autoLoginCapability?.type !== device.autoLoginCapability?.type) {
				this._router.navigate(["/auto-login"], {onSameUrlNavigation: "reload"});
			}

			this.device.set(device);
		});
		this._eventEmitter.onDeviceStateChanged.subscribe(({deviceState}) => this.deviceState.set(deviceState));
		this._eventEmitter.onDeviceSettingChanged.subscribe(({deviceSettings}) => {
			this._translate.use(deviceSettings.currentLanguage);
		});

		const device = await this._deviceApiService.registerDevice();
		this.device.set(device);

		const deviceIdChanged = this._injectableDeviceDataService.clientData?.deviceId !== device.id;
		if (deviceIdChanged) {
			this._injectableDeviceDataService.setDeviceId(device.id);
			await this._systemHub.reconnect();
		}

		timer(0, 60_000).subscribe(async () => await this.updateDeviceData());
	}

	private async updateDeviceData() {
		if (this._systemHub.isConnected()) {
			const ipAddresses = this._injectableDeviceDataService.networkInterfaces.map((x) => x.address);
			const macAddresses = this._injectableDeviceDataService.networkInterfaces.map((x) => x.mac);

			await this._systemHub.updateDeviceData({
				computerName: this._injectableDeviceDataService.hostData?.hostname,
				ipAddresses: ipAddresses,
				macAddresses: macAddresses,
			});
		}
	}
}
